import {
  Navigate,
} from "react-router-dom";
import React, { useState, useEffect, useContext, useCallback } from 'react';
import Template from '../components/Template';
import MusicBox from '../components/MusicBox';
import DateBox from '../components/DateBox';
import AppContext from "../store";
import { Music } from "../store/types";
import { useDebounce } from "../utils/debounce";
import { displayDate } from "../store/utils";
import { message, Input } from "antd";
import { LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";

const { Search } = Input;

type SearchSceneType = {
  onClick?: Function;
};

const SearchScene = (props: SearchSceneType) => {
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();

  const {
    auth,
    loadingSearch,
    searchResults,
    searchMusic,
    selectMusic,
    deleteMusic,
  } = useContext(AppContext);

  const debouncedSearchValue = useDebounce(searchValue, 1000);

  useEffect(() => {
    if (debouncedSearchValue && debouncedSearchValue.trim()) {
      searchMusic(debouncedSearchValue);
    }
    // Note: We don't clear results when search is empty to keep them visible
    // until user types a new search
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchValue]);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleDelete = async (id: string) => {
    const res = await deleteMusic(id);
    if (res && res.code) {
      message.error('Fail during process');
    } else {
      message.success('Song deleted');
      // Refresh search results after deletion
      if (debouncedSearchValue) {
        searchMusic(debouncedSearchValue);
      }
    }
  }

  const sceneLogged = (
    <Template logged={!!auth}>
      <div style={{ padding: 24, minHeight: '90vh', background: 'rgb(0, 45, 87' }}>
        <h2 style={{ marginBottom: 0, fontFamily: 'Shadows Into Light', fontSize: '50px', color: '#ffcb32' }}>SONG REPO</h2>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', maxWidth: '1046px', width: '100%', margin: 'auto' }}>
          {!loadingSearch && 
            <Search
              style={{
                fontSize: '22px',
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                borderRadius: '13px',
                background: 'white',
                overflow: 'hidden',
                height: '51px',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
              }}
              bordered={false}
              placeholder="Search by title, author, album, tags..."
              onChange={(elt: React.ChangeEvent<HTMLInputElement>) => handleSearch(elt.target.value)}
              onSearch={value => handleSearch(value)}
              value={searchValue}
            />
          }
        </div>
        
        {!loadingSearch && searchResults !== null && searchResults.length === 0 && searchValue.trim() && (
          <p style={{ color: 'white', fontSize: '25px', marginTop: '30px', textAlign: 'center' }}>
            No results found for "{searchValue}"
          </p>
        )}

        {!loadingSearch && searchResults && searchResults.length > 0 && (() => {
          // Sort results by year (descending) and then by date (descending)
          const sortedResults = [...searchResults].sort((a, b) => {
            const yearA = new Date(a.date).getFullYear();
            const yearB = new Date(b.date).getFullYear();
            if (yearA !== yearB) {
              return yearB - yearA; // Newest year first
            }
            return new Date(b.date).getTime() - new Date(a.date).getTime(); // Newest date first within same year
          });

          // Group by year
          const groupedByYear = sortedResults.reduce((acc, music) => {
            const year = new Date(music.date).getFullYear();
            if (!acc[year]) {
              acc[year] = [];
            }
            acc[year].push(music);
            return acc;
          }, {} as Record<number, Music[]>);

          const years = Object.keys(groupedByYear).map(Number).sort((a, b) => b - a);

          return (
            <div style={{
              maxWidth: '1090px',
              margin: 'auto',
              marginTop: '30px',
            }}>
              <div style={{ marginBottom: '20px' }}>
                <p style={{ color: 'white', fontSize: '20px', marginBottom: '5px' }}>
                  Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                </p>
              </div>
              {years.map((year) => (
                <div 
                  key={year} 
                  style={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '30px',
                    gap: '20px',
                  }}
                >
                  {/* Left column: Year */}
                  <div style={{
                    background: 'black',
                    borderRadius: '5px',
                    padding: '23px 20px',
                    minWidth: '120px',
                    textAlign: 'center',
                    flexShrink: 0,
                  }}>
                    <h3 style={{
                      color: '#ffcb32',
                      fontSize: '28px',
                      margin: 0,
                      fontWeight: 'bold',
                    }}>
                      {year}
                    </h3>
                  </div>
                  
                  {/* Right column: Musics */}
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    alignItems: 'end',
                  }}>
                    {groupedByYear[year].map((elt, key) => (
                      <div
                        key={key}
                        style={{
                          maxWidth: '330px',
                          width: '100%',
                          display: 'inline-block',
                        }}
                      >
                        {key === 0 && 
                          <DateBox
                            date={displayDate(elt.date)}
                          />}
                        {key > 0 && elt.date !== groupedByYear[year][key - 1].date &&
                          <DateBox
                            date={displayDate(elt.date)}
                          />}
                        <MusicBox
                          music={elt}
                          selectMusic={(music: Music) => {
                            selectMusic(music);
                            navigate("/edit", { replace: true });
                          }}
                          deleteMusic={handleDelete}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {loadingSearch && <LoadingOutlined style={{ marginTop: '30px', color: 'white', display: 'block', fontSize: '60px' }} />}
      </div>
    </Template>
  )

  const redirect = (
    <Navigate
      to={`/login`}
    />
  );

  return auth ? sceneLogged : redirect;
}

export default SearchScene;